import { UrlHelper } from "../html/url-helper";
import { Logger } from "../../logger/logger";
import { BaseModel } from "../../models/base-model";
import { BaseDto } from "../../models/base-dto";
import { KoExtend } from "../../extend/knockout-extend";
import { ServiceProvider } from "../provider/service-provider";
import { diff } from "../../extend/diff";

export interface ISqafIcon {
	id: number;
	fileName: number;
	icotId: number;
}

export class Debugging {
	public static getInstance = () => {
		let myWindow: { _debug: Debugging } = window as any;
		if (!myWindow._debug) {
			myWindow._debug = new Debugging();
		}
		return myWindow._debug;
	}

	constructor() {
		if (this.isEnabled()) {
			this.validate();
			window.setTimeout(() => this.autoReloadAsync(), 3000);
		}
	}

	public isEnabled = Logger.isEnabled;
	public data: { [name: string]: DebugItem } = {};
	public autoReloadEnabled = false;
	public autoReloadRetries = 0;
	public readonly autoReloadMaxRetries = 3;
	public logout = () => {
		location.href = UrlHelper.getBaseUrl() + "Authentication/Logout";
	}

	public validate = () => {
		$(() => {
			this.validateUniqueIds();
			this.validateNoSemicolon();
		});
	}

	public autoReloadThread = async () => {
		if (!this.autoReloadEnabled) {
			Logger.log("Auto reload disabled");
			return;
		}
		let url = UrlHelper.getDataUrl("notify-on-file-version-changed");
		let success = await ServiceProvider.ajax.postJsonAsync({ url, timeout: 0 }).catch(() => null);
		if (success) {
			this.autoReloadRetries = this.autoReloadMaxRetries;
			location.reload();
		} else {
			this.autoReloadRetries++;
			if (this.autoReloadRetries > this.autoReloadMaxRetries) {
				this.autoReloadEnabled = false;
				Logger.warn("Auto reload disabled due to maximum retries reached");
				return;
			}
			this.autoReloadThread();
		}
	}

	public autoReloadAsync = async () => {
		let win: any = window;
		if (win.nautilusAutoReload !== true) {
			return;
		}
		this.autoReloadEnabled = true;
		Logger.log("Auto reload enabled");
		this.autoReloadThread();
	}

	public diff = () => {
		Logger.beginGroup("Diff");
		for (let name of Object.keys(this.data)) {
			Logger.beginGroup(name);
			this.data[name].diff();
			Logger.endGroup();
		}
		Logger.endGroup();
	}

	public validateUniqueIds() {
		let foundError = false;

		$("[id]")
			.toArray()
			.map((element) => $(element).attr("id"))
			.map((id, index, array) => {
				if (array.indexOf(id) !== index && array.lastIndexOf(id) === index) {
					if (!foundError) {
						Logger.beginGroupCollapsed("ValidateUniqueIds");
						foundError = true;
					}

					Logger.warn(`Id "${id}" is not unique:`, $(`[id="${id}"]`).toArray());
				}
			});

		if (foundError) {
			Logger.endGroup();
		}
	}

	public validateNoSemicolon() {
		let walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);
		while (true) {
			let node = walker.nextNode();
			if (!node) { break; }

			if (node.parentElement && node.parentElement.tagName !== "SCRIPT" && node.parentElement.tagName !== "STYLE") {
				let text = (node.textContent || "").trim();
				for (let symbol of [";", "{", "}"]) {
					if (text.startsWith(symbol) || text.endsWith(symbol)) {
						Logger.warn("Found semicolon/brace: ", node.parentElement, text);
					}
				}
			}
		}
	}

	public validateDataAttributes(selectors: string[] = ["#tabs", "#body"]) {
		for (let selector of selectors) {
			let element = $(selector);
			if (element.length !== 0) {
				let values = element.data();
				for (let key of Object.keys(values)) {
					let value = values[key];
					if (value === "") {
						Logger.warn(`data attribute ${key} of '${selector}' is '${value}'`);
					}
				}
			}
		}
	}

	public registerData(key: string, fn: () => any) {
		if (!this.isEnabled()) {
			return;
		}

		let item = new DebugItem(fn);
		this.data[key] = item;
	}

	public printDom(elem: HTMLElement | JQuery<HTMLElement>) {
		let nodes = $(elem)
			.parents("[data-bind], [with], [id]")
			.toArray()
			.reverse();
		nodes.push(...$(elem).get());

		nodes = nodes.map((element) => {
			let result: any = {
				element,
				"id": $(element).attr("id"),
				"data-bind": $(element).attr("data-bind"),
				"with": $(element).attr("with"),
			};

			return result;
		});

		Logger.table(nodes);
		return nodes;
	}
}

export class DebugItem {
	constructor(public readonly fn: () => any) { }

	public get view() {
		return ko.toJS(this.fn());
	}

	public get raw() {
		return this.fn();
	}

	public diff() {
		let model: BaseModel<BaseDto> = KoExtend.resolve(this.fn());
		if (model && model.dirtyFlag) {
			let { initial, current } = model.dirtyFlag.getDiff();
			let result = diff(initial, current, (a, b) => a.key === b.key && a.value === b.value)
				.map((item, index) => {
					return { index, type: item.type, key: item.value.key, value: item.value.value };
				});

			let firstChange = result.findIndex((item) => item.type !== "=");
			let lastChange = -1;
			for (let i = result.length - 1; i >= 0; i--) {
				if (result[i].type !== "=") {
					lastChange = i;
					break;
				}
			}

			if (firstChange !== -1 && lastChange !== -1) {
				const windowSize = 1;
				Logger.table(result.slice(firstChange - windowSize, lastChange + 1 + windowSize));
				return;
			}

			Logger.log("No changes found");
		}
	}
}

$(() => Debugging.getInstance());